import type { ReactNode } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SearchInput } from '@/components/search-input'

/** What a conversation row says: what it is, who it is with, and where it stands. */
export function ConversationListItemContent({
  title,
  meta,
  badges,
  selected,
  muted = false,
  aside,
}: {
  title: ReactNode
  /** Counterpart and time, one muted line. */
  meta: ReactNode
  badges?: ReactNode
  selected: boolean
  /** A closed or finished conversation reads quieter until it is opened. */
  muted?: boolean
  /** Right of the title, e.g. an unread count. */
  aside?: ReactNode
}) {
  return (
    <>
      {/* Inside a narrow PO conversations container the row is compact (see po-conversations). */}
      <span className="flex items-start gap-2 @max-2xl/conversations:w-full">
        <span
          className={cn(
            'line-clamp-2 min-w-0 flex-1 text-[13px] leading-snug @max-2xl/conversations:line-clamp-1',
            selected ? 'font-semibold text-foreground' : 'font-medium',
            muted && !selected ? 'text-muted-foreground' : 'text-foreground',
          )}
        >
          {title}
        </span>
        {aside}
      </span>
      <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
        {meta}
      </span>
      {badges && (
        <span className="flex flex-wrap items-center gap-1.5 [&_[data-slot=badge]]:text-[10px]">{badges}</span>
      )}
    </>
  )
}

/** Search box above a conversation list; Escape clears it. */
export function ConversationSearch({
  value,
  onChange,
  placeholder = 'Search conversations',
  className,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}) {
  return (
    <label className={cn('relative flex items-center', className)}>
      <span className="sr-only">{placeholder}</span>
      <Search
        aria-hidden
        className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-muted-foreground"
      />
      <SearchInput bare
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onChange('')
        }}
        placeholder={placeholder}
        className="h-8 w-full min-w-0 rounded-md border border-input bg-background pl-8 pr-8 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-1.5 rounded p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </label>
  )
}
